import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import classNames from 'styles/utils/classNames';
import { useCurrentUser } from 'hooks/useCurrentUser';
import resolveUserTimezone from 'utils/shared/time/resolveUserTimezone';
import { useMarketClockMs } from 'hooks/useMarketSessionClock';
import {
  marketSessionPhase,
  nextCoverageWeekday,
  nextTapeWeekday,
  type MarketSessionPhase,
} from 'utils/shared/time/marketSession';
import MarketDataPanel from './MarketDataPanel';
import StatusDot from './StatusDot';
import {
  PENDING_LABEL,
  TRIGGER_LABEL,
  type FlowAccess,
  type MarketDataSurface,
} from './marketDataStatusModel';

interface Props {
  surface: MarketDataSurface;
  /** The viewer's options-flow entitlement. Defaults to `unknown`, which says nothing about a plan. */
  flowAccess?: FlowAccess;
  /** The ET session of the open interest on screen; the GEX surface's Open Interest row reads it. */
  oiSessionDate?: string | null;
  className?: string;
}

interface SessionSnapshot {
  phase: MarketSessionPhase;
  coverageWeekday: string | null;
  tapeWeekday: string | null;
  /** The instant the snapshot was read, so window conversion lands on the right DST offset. */
  nowMs: number;
}

/**
 * The wall clock, read only on the client.
 *
 * Three of the four host pages are pre-rendered — the GEX routes are ISR on a 30-minute revalidate
 * — so anything this component renders on the server can be half an hour stale by the time a
 * visitor sees it. Reading `Date.now()` during render, or in a `useState` initializer, would bake a
 * build-time session into that HTML and mismatch during hydration; a mismatch this high in the tree
 * makes React discard the whole SSR root rather than patch it. So: `null` until mounted, and every
 * phase-dependent string derives from this snapshot.
 */
const useMarketSession = (): SessionSnapshot | null => {
  // The one app-wide session clock (see `useMarketSessionClock`) rather than a private interval, so
  // this dot, the live-price dot and the auto-refresh pill can never report different sessions at a
  // boundary. `null` until the clock is first read on the client — see the hydration note above.
  const nowMs = useMarketClockMs();

  return React.useMemo(() => {
    if (nowMs === null) return null;
    return {
      phase: marketSessionPhase(nowMs),
      coverageWeekday: nextCoverageWeekday(nowMs),
      tapeWeekday: nextTapeWeekday(nowMs),
      nowMs,
    };
  }, [nowMs]);
};

/**
 * When and where each feed updates — one control, one vocabulary, in the same place on every page.
 *
 * Replaces every competing freshness grammar in the product: a pulsing "Live" flow pill that never
 * turned off, a holiday-blind "Live"/"Closed" badge, and the hand-rolled variations around them. If
 * this is mounted on a page, nothing else there may claim liveness.
 */
export default function MarketDataStatus({
  surface,
  flowAccess = 'unknown',
  oiSessionDate = null,
  className,
}: Props) {
  const [open, setOpen] = React.useState(false);

  /*
    THE EXCHANGE'S CALENDAR, NOT OURS.

    Scheduled holidays are arithmetic and the computed rules get them right. Ad-hoc closures are
    announcements — Jimmy Carter's funeral, Hurricane Sandy — and no rule derives those. The
    app-root `MarketCalendarProvider` fetches the published list once and installs it into the pure
    session model, so `marketSessionPhase` below already reflects it and the dot cannot go green on a
    day the market is shut. Nothing to call for it here — the session read is enough.
  */
  const session = useMarketSession();

  /*
    THE WINDOWS ARE SHOWN IN THE VIEWER'S OWN CLOCK.

    Everything here is a fact about the US session, so it is stored in ET — but a trader who has set
    the app to local time reads every other timestamp in local time, and making them convert 9:30 AM
    ET in their head is the confusion this control exists to remove. `resolveUserTimezone` is the
    same preference the flow graphs already read, so the whole app agrees.
  */
  const { user } = useCurrentUser();
  const zone = React.useMemo(
    () => resolveUserTimezone(user?.timezoneLocation),
    [user?.timezoneLocation],
  );

  const resolved = session !== null;
  const phase: MarketSessionPhase = session?.phase ?? 'closed';
  const label = resolved ? TRIGGER_LABEL[phase] : PENDING_LABEL;
  const isLive = resolved && phase !== 'closed';
  const weekday =
    phase === 'closed' ? session?.coverageWeekday ?? null : session?.tapeWeekday ?? null;

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      flip({ fallbackPlacements: ['bottom-end', 'top-start', 'top-end'] }),
      shift({ padding: 10 }),
    ],
  });

  /*
    HOVER FOR A MOUSE, TAP FOR A FINGER.

    `mouseOnly` keeps the hover handler off touch devices, where a synthesised hover would open the
    panel on the same tap that is meant to toggle it; `useClick` then carries touch and keyboard.
    `safePolygon` lets the pointer cross the 10px gap into the panel without it closing underneath.

    Position is `fixed` in a body portal because the page header is height-locked inside an
    `overflow-hidden` wrapper — an absolutely-positioned panel would be clipped to a 40px strip.
  */
  const hover = useHover(context, {
    mouseOnly: true,
    handleClose: safePolygon({ blockPointerEvents: false }),
  });
  const click = useClick(context);
  const dismiss = useDismiss(context, { outsidePress: true });
  const role = useRole(context, { role: 'dialog' });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, click, dismiss, role]);

  return (
    <>
      <button
        ref={refs.setReference}
        {...getReferenceProps()}
        type="button"
        data-market-data-status={surface}
        data-session-phase={resolved ? phase : 'pending'}
        aria-label={
          resolved
            ? `Market data — ${label}. Open the feed schedule.`
            : `${PENDING_LABEL}. Open the feed schedule.`
        }
        aria-expanded={open}
        title={resolved ? `${label} — see when each feed updates` : 'See when each feed updates'}
        className={classNames(
          /*
            THE DOT IS THE WHOLE CONTROL.

            No label. These headers already carry a title, tabs, filters and CTAs, and they will
            only get busier — a ~110px "Markets open" pill is the first thing that would have to be
            cut, on the page where it matters most. At 30px this fits anywhere and stays put.

            The chevron is what keeps it legible as a control rather than decoration: a bare dot
            reads as an indicator, a dot with a disclosure caret reads as something you can open.
            The session itself is one hover away, and the accessible name carries it for anyone who
            cannot see colour at all.
          */
          'inline-flex h-[26px] shrink-0 items-center gap-1 rounded-full border border-white/[0.09] bg-white/[0.03] px-1.5',
          'leading-none text-text-secondary',
          'transition-colors duration-150 hover:border-white/25 hover:bg-white/[0.08] hover:text-white',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue/70',
          className,
        )}
      >
        <StatusDot phase={phase} live={isLive} />
        <svg aria-hidden="true" viewBox="0 0 12 12" className="h-2.5 w-2.5 shrink-0 opacity-60">
          <path
            d="m3 4.5 3 3 3-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-[60]"
          >
            <MarketDataPanel
              surface={surface}
              phase={phase}
              flowAccess={flowAccess}
              weekday={weekday}
              resolved={resolved}
              zone={zone}
              nowMs={session?.nowMs ?? 0}
              oiSessionDate={oiSessionDate}
            />
          </div>,
          document.body,
        )}
    </>
  );
}
